import ToggleButton from '../../../components/common/toggle-button';
import React, { useState, useEffect } from 'react';
import AccordionTile from '../Accordion';
import { DropDown } from '../../../components/common';
import {
  Package,
  PackageProduct,
  ProductTemplate,
  ProductTemplateKey,
  useCreatePackageProduct,
  useUpdatePackageProduct,
} from '../../../hooks/useClients';

type RenewalPeriod = 'monthly' | 'yearly' | 'no_renewal';

interface CappedAccessProps {
  packageData: Package;
  defaultTemplate: ProductTemplate;
  setSaveDataModal: React.Dispatch<React.SetStateAction<boolean>>;
}

const CappedAccess: React.FunctionComponent<CappedAccessProps> = ({
  packageData,
  defaultTemplate,
  setSaveDataModal,
}): JSX.Element => {
  const selectedPackageProduct = packageData.package_products.find(
    (item: PackageProduct) => item.product.key === ProductTemplateKey.CAPPED_ACCESS,
  );
  const { createPackageProduct } = useCreatePackageProduct(
    packageData.client_id!,
    selectedPackageProduct?.package_id! || packageData.id!,
    defaultTemplate.id,
  );
  const { updatePackageProduct } = useUpdatePackageProduct(packageData.client_id!, selectedPackageProduct?.id!);

  const [hidePaymentSetupNotice, setHidePaymentSetupNotice] = useState(true);
  const [cappedAccessTab, setCappedAccessTab] = useState(false);
  const [isCallUnit, setisCallUnit] = useState(false);
  const [monthlyRenewals, setMonthlyRenewals] = useState<number>(0);
  const [minutesPerMember, setMinutesPerMember] = useState(0);
  const [reservedBalance, setReservedBalance] = useState(0);
  const [enableSaveOption, setEnableSaveOption] = useState(false);
  const [errorMessage, setErrorMessage] = useState<any>({});
  const [renewalPeriod, setRenewalPeriod] = useState<RenewalPeriod>('no_renewal' as RenewalPeriod);

  const Validation = () => {
    if (cappedAccessTab) {
      if (isCallUnit && minutesPerMember! % 30) {
        setErrorMessage({ minutesPerMember: 'Minutes per member must be a multiple of 30' });
        return false;
      }
      if (!minutesPerMember) {
        setErrorMessage({ minutesPerMember: 'Field is required' });
        return false;
      } else if (minutesPerMember.toString().indexOf('.') !== -1) {
        setErrorMessage({
          minutesPerMember: 'Number may not include decimals - whole numbers only',
        });
        return false;
      } else if (+minutesPerMember <= 0) {
        setErrorMessage({ minutesPerMember: 'Must be a number greater than 0' });
        return false;
      } else if (renewalPeriod !== 'no_renewal' && !monthlyRenewals && monthlyRenewals !== 0) {
        setErrorMessage({ monthlyRenewals: 'Field is required' });
        return false;
      } else if (monthlyRenewals.toString().indexOf('.') !== -1) {
        setErrorMessage({
          monthlyRenewals: 'Number may not include decimals - whole numbers only',
        });
        return false;
      } else if (renewalPeriod !== 'no_renewal' && (monthlyRenewals < 0 || monthlyRenewals > 60)) {
        setErrorMessage({ monthlyRenewals: 'Must be a number between 1 and 60' });
        return false;
      }
    }
    setErrorMessage({});
    return true;
  };

  useEffect(() => {
    if (packageData) {
      const configuration = selectedPackageProduct
        ? selectedPackageProduct.configuration
        : defaultTemplate.configuration;
      setCappedAccessTab(selectedPackageProduct?.status === 'enabled' ? true : false);
      setisCallUnit(configuration.is_call_units || false);
      setHidePaymentSetupNotice(configuration.hide_payment_tiles || false);
      setMinutesPerMember(Number(configuration.maximum_minutes_per_user));
      setMonthlyRenewals(Number(configuration.auto_renew_maximum_uses || 0));
      setReservedBalance(selectedPackageProduct ? selectedPackageProduct.unused_minute_balance : 0);
      setRenewalPeriod((configuration.auto_renew_frequency || 'no_renewal') as RenewalPeriod);
      setEnableSaveOption(false);
    }
  }, [packageData]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (renewalPeriod === 'no_renewal' && monthlyRenewals > 0) {
      setMonthlyRenewals(0);
    }
  }, [renewalPeriod, monthlyRenewals]);

  useEffect(() => {
    setSaveDataModal(enableSaveOption);
  }, [enableSaveOption]);

  const onSave = () => {
    if (Validation()) {
      const payload: Partial<PackageProduct> = {
        status: cappedAccessTab ? 'enabled' : 'disabled',
        configuration: {
          expires_after: undefined,
          hide_payment_tiles: hidePaymentSetupNotice,
          auto_renew_frequency: renewalPeriod,
          auto_renew_maximum_uses: Number(monthlyRenewals),
          maximum_minutes_per_user: Number(minutesPerMember),
          maximum_redemptions_allowed: 0,
          is_call_units: isCallUnit,
        },
      };
      if (selectedPackageProduct) {
        updatePackageProduct.mutate(payload);
      } else {
        createPackageProduct.mutate(payload);
      }
    }
  };

  const getMinutesPerPeriodLabel = () => {
    const suffix = ['monthly', 'yearly'].includes(renewalPeriod) ? ` per ${renewalPeriod.replace('ly', '')}` : '';
    return `MAX member usage${suffix}`;
  };
  const minutePeriodOptions = [
    { label: 'Monthly', value: 'weekly' },
    { label: 'Yearly', value: 'yearly' },
    { label: 'No Renewal', value: 'no_renewal' },
  ];
  return (
    <>
      <AccordionTile
        setStatus={(data: boolean) => {
          if ((!data && Validation()) || data) {
            !enableSaveOption && setEnableSaveOption(true);
            setCappedAccessTab(data);
          }
        }}
        // disableToggleButton={Object.keys(errorMessage).length > 0 ? true : false}
        Label={'CAPPED ACCESS'}
        buttonText={'SAVE'}
        status={cappedAccessTab}
        id={'CappedAcess'}
        disableButton={enableSaveOption}
        onButtonClick={() => {
          if (enableSaveOption) onSave();
        }}
        initialstatus={true}
        InnerComponent={
          <div className="pb-10">
            <div className="flex">
              <div className="w-1/3 px-2 pt-2">
                <div>
                  <label className="text-blue-dark mb-2 block pt-2 text-sm font-medium leading-4">
                    {getMinutesPerPeriodLabel()}
                  </label>
                  <input
                    type="text"
                    onWheel={(e: any) => e.target.blur()}
                    min={0}
                    value={minutesPerMember}
                    className="text-gray-dark bg-gray-background-light w-1/2 rounded-md px-3 py-2 text-sm font-extrabold leading-4 focus:ring-transparent"
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '');
                      if (val !== minutesPerMember.toString()) {
                        !enableSaveOption && setEnableSaveOption(true);
                      }
                      setMinutesPerMember(Number(val));
                    }}
                  />
                  {errorMessage?.minutesPerMember && (
                    <label className="text-red mb-2 block pt-2 text-sm font-medium leading-4">
                      {errorMessage.minutesPerMember}
                    </label>
                  )}
                </div>
              </div>
              <div className="flex-between flex w-1/3 px-2">
                <div className="mx-2 mt-2">
                  <label className="text-blue-dark mb-2 block pt-2 text-sm font-medium leading-4">Renewal Period</label>
                  <DropDown
                    Label=""
                    value={renewalPeriod}
                    setValue={(data) => {
                      !enableSaveOption && setEnableSaveOption(true);
                      setRenewalPeriod(data);
                    }}
                  >
                    {minutePeriodOptions.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </DropDown>
                </div>
              </div>
              <div
                className={'flex-start flex w-1/3 px-2 ' + (renewalPeriod === 'no_renewal' && ' cursor-not-allowed')}
              >
                <div className="mx-2 mt-2">
                  <label className="text-blue-dark mb-2 block pt-2 text-sm font-medium leading-4">
                    {renewalPeriod === 'no_renewal'
                      ? 'No'
                      : minutePeriodOptions.find((item) => item.value === renewalPeriod)?.label}{' '}
                    Renewals
                  </label>
                  <div className="flex">
                    <input
                      type="text"
                      onWheel={(e: any) => e.target.blur()}
                      min={0}
                      disabled={renewalPeriod === 'no_renewal'}
                      value={monthlyRenewals}
                      className={
                        'text-gray-dark bg-gray-background-light w-1/6 rounded-md px-3 py-2 text-sm font-extrabold leading-4 focus:ring-transparent ' +
                        (renewalPeriod === 'no_renewal' && ' pointer-events-none cursor-not-allowed')
                      }
                      onChange={(e: any) => {
                        const val = Number(e.target.value.replace(/\D/g, ''));
                        if (val !== monthlyRenewals) {
                          !enableSaveOption && setEnableSaveOption(true);
                        }
                        setMonthlyRenewals(Number(val));
                      }}
                    />
                    <p className="text-gray-dark pl-3 pt-1 font-medium">Enter 0 for no renewal limit</p>
                  </div>
                  {errorMessage?.monthlyRenewals && (
                    <label className="text-red mb-2 block pt-2 text-sm font-medium leading-4">
                      {errorMessage.monthlyRenewals}
                    </label>
                  )}
                </div>
              </div>
            </div>
            <div className="flex w-full justify-start px-2 pt-2">
              <div className="flex w-1/3 items-center">
                <p className="text-blue-dark text-sm font-medium">Reserved Balance: {reservedBalance} minutes</p>
              </div>
              <div className="flex w-1/3 items-center">
                <p className="text-blue-dark px-4 text-sm font-medium">Hide Payment Setup Notice</p>
                <ToggleButton
                  setStatus={(data) => {
                    !enableSaveOption && setEnableSaveOption(true);
                    setHidePaymentSetupNotice(data);
                  }}
                  status={hidePaymentSetupNotice}
                  id={'hidepaymentStatus'}
                  initialstatus={true}
                />
              </div>
              <div className="flex w-1/3 items-center">
                <p className="text-blue-dark px-4 py-2 text-sm font-medium">Using Call Units</p>
                <ToggleButton
                  status={isCallUnit}
                  setStatus={(data) => {
                    !enableSaveOption && setEnableSaveOption(true);
                    setisCallUnit(data);
                  }}
                  initialstatus={false}
                  id={'Is Call Units'}
                />
              </div>
            </div>
            {!selectedPackageProduct && (
              <p className="text-blue-dark mb-2 block pt-2 text-sm font-medium leading-4">
                You must save this product to add credit
              </p>
            )}
          </div>
        }
      />
    </>
  );
};

export default CappedAccess;
